/**
 * Bolsas de vacaciones de un trabajador: ver, crear, corregir y cerrar.
 *
 * Una bolsa es un período con su derecho, sus días consumidos y su saldo. El
 * backend ya exponía la escritura desde hace meses y ninguna pantalla la usaba,
 * así que las bolsas solo podían nacer del importador: a quien la migración no
 * le trajo la bolsa progresiva no había forma de creársela (#212, #326).
 *
 * 🔴 `days_taken` es de solo lectura, acá y en el backend. Lo mueve la
 * aprobación de cada solicitud, y es lo que mantiene el saldo cuadrado con el
 * historial que se muestra justo abajo. Editarlo a mano los desalinearía sin
 * que nada lo delate.
 */
import { useState } from 'react'
import { Plus, Pencil, Trash2, Wallet } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'

import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { extractErrorMessage } from '@/lib/errorUtils'
import { formatDate } from '@/lib/locale'
import { inputCls, labelCls } from '@/lib/formStyles'
import { usePermission } from '@/hooks/usePermission'
import { useToast } from '@/components/Toast'
import DatePicker from '@/components/DatePicker'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import {
  useCreateVacationRecord, useDeleteVacationRecord, useUpdateVacationRecord,
  useVacationRecords, type VacationRecordPayload,
} from '../hooks/usePayroll'

const TIPOS = [
  { value: 'legal', label: 'Feriado legal (Art. 67)' },
  { value: 'progressive', label: 'Feriado progresivo (Art. 68)' },
] as const

export default function VacationRecordsCard({ employeeId, locale, hireDate }: {
  employeeId: string
  locale: string
  hireDate?: string
}) {
  const { can } = usePermission()
  const { data } = useVacationRecords(employeeId)
  const [editando, setEditando] = useState<any | null>(null)
  const [creando, setCreando] = useState(false)
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const { mutate: borrar } = useDeleteVacationRecord(employeeId)

  const bolsas = data?.records || data?.results || []
  const puedeEditar = can('payroll.vacations', 'edit')
  const puedeCrear = can('payroll.vacations', 'create')

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider flex items-center gap-1.5">
          <Wallet size={12} /> Bolsas
        </h3>
        {puedeCrear && !creando && (
          <button onClick={() => { setCreando(true); setEditando(null) }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
            <Plus size={12} /> Nueva bolsa
          </button>
        )}
      </div>

      {(creando || editando) && (
        <BolsaForm
          employeeId={employeeId} bolsa={editando} hireDate={hireDate} toast={toast}
          onClose={() => { setCreando(false); setEditando(null) }}
        />
      )}

      <DataTable<any>
        columns={[
          { key: 'vacation_type_display', header: 'Tipo', sortable: true,
            render: b => <span className="text-content">{b.vacation_type_display}</span> },
          { key: 'period_start', header: 'Período', sortable: true,
            render: b => (
              <span className="text-content-muted">
                {formatDate(b.period_start, locale)} → {formatDate(b.period_end, locale)}
              </span>
            ) },
          { key: 'days_entitled', header: 'Derecho', numeric: true, sortable: true,
            sortValue: b => Number(b.days_entitled) },
          // ⚠️ «Tomados» es de solo lectura también acá: lo mueve la aprobación de
          // cada solicitud, y es lo que mantiene el saldo cuadrado con el historial.
          { key: 'days_taken', header: 'Tomados', numeric: true, sortable: true,
            sortValue: b => Number(b.days_taken),
            render: b => <span className="text-content-muted">{b.days_taken}</span> },
          { key: 'days_pending_accrued', header: 'Saldo', numeric: true, sortable: true,
            sortValue: b => Number(b.days_pending_accrued),
            render: b => <span className="font-semibold">{b.days_pending_accrued}</span> },
          { key: 'status', header: 'Estado', sortable: true,
            render: b => (
              <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                b.status === 'active'
                  ? 'bg-success-surface-strong text-success-content'
                  : 'bg-surface-muted text-content-subtle'
              }`}>{b.status === 'active' ? 'Activa' : 'Cerrada'}</span>
            ) },
          ...(puedeEditar ? [{
            key: 'acciones', header: '', width: 72,
            render: (b: any) => (
              <div className="flex justify-end gap-1">
                <button onClick={() => { setEditando(b); setCreando(false) }}
                  title="Corregir" className="p-1 rounded hover:bg-soft text-content-muted">
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => confirm({
                    message: '¿Eliminar esta bolsa? Solo se puede si no tiene vacaciones imputadas.',
                    variant: 'danger',
                    onConfirm: () => borrar(b.id, {
                      onSuccess: () => toast.success('Bolsa eliminada'),
                      onError: (e: any) => toast.error(extractErrorMessage(
                        e, 'No se pudo eliminar la bolsa.')),
                    }),
                  })}
                  title="Eliminar" className="p-1 rounded hover:bg-danger-surface text-danger-solid">
                  <Trash2 size={13} />
                </button>
              </div>
            ),
          }] : []),
        ]}
        rows={bolsas}
        rowKey={b => b.id}
        minWidth={760}
        empty={<EmptyState title="Sin bolsas registradas." size="sm" />}
        caption="Bolsas de feriado del trabajador"
      />

      <p className="mt-2 text-xs text-content-subtle">
        El saldo suma lo devengado desde el fin del período. Los días tomados salen de las
        solicitudes aprobadas y no se editan acá.
      </p>

      <ConfirmModal {...modalProps} />
    </div>
  )
}


function BolsaForm({ employeeId, bolsa, hireDate, onClose, toast }: {
  employeeId: string
  bolsa: any | null
  hireDate?: string
  onClose: () => void
  toast: any
}) {
  const editando = !!bolsa
  const [tipo, setTipo] = useState<'legal' | 'progressive'>(bolsa?.vacation_type || 'legal')
  const [desde, setDesde] = useState(bolsa?.period_start || hireDate || '')
  const [hasta, setHasta] = useState(bolsa?.period_end || '')
  const [derecho, setDerecho] = useState(bolsa?.days_entitled ?? '')
  const [estado, setEstado] = useState(bolsa?.status || 'active')
  const [error, setError] = useState<string | null>(null)

  const { mutate: crear, isPending: creando } = useCreateVacationRecord(employeeId)
  const { mutate: actualizar, isPending: actualizando } = useUpdateVacationRecord(employeeId)

  const guardar = () => {
    setError(null)
    if (!desde || !hasta || derecho === '') {
      setError('Completar el período y los días de derecho.')
      return
    }
    const payload: VacationRecordPayload = {
      employee: employeeId, vacation_type: tipo,
      period_start: desde, period_end: hasta,
      days_entitled: String(derecho), status: estado,
    }
    const listo = {
      onSuccess: () => { toast.success(editando ? 'Bolsa actualizada' : 'Bolsa creada'); onClose() },
      onError: (e: any) => setError(extractErrorMessage(e, 'No se pudo guardar la bolsa.')),
    }
    if (editando) actualizar({ id: bolsa.id, ...payload }, listo)
    else crear(payload, listo)
  }

  return (
    <div className="mb-3 p-4 bg-glass-3 border border-divider rounded-xl space-y-3">
      <p className="text-xs font-bold text-content-muted uppercase tracking-wider">
        {editando ? 'Corregir bolsa' : 'Nueva bolsa'}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Tipo</label>
          {/* El tipo no se cambia en una bolsa existente: sus solicitudes ya se
              imputaron a esa clase de feriado y moverlo las dejaría en la otra. */}
          <select aria-label="Tipo de bolsa" value={tipo} disabled={editando}
            onChange={e => setTipo(e.target.value as 'legal' | 'progressive')}
            className={`${inputCls} disabled:opacity-60`}>
            {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="vacationrecordscard-desde" className={labelCls}>Desde</label>
          <DatePicker id="vacationrecordscard-desde" value={desde} onChange={setDesde} />
        </div>
        <div>
          <label htmlFor="vacationrecordscard-hasta" className={labelCls}>Hasta</label>
          <DatePicker id="vacationrecordscard-hasta" value={hasta} onChange={setHasta} />
        </div>
        <TextField
          dense
          label="Días de derecho"
          value={derecho}
          onChange={e => setDerecho(e.target.value)}
          inputProps={{ type: 'number', step: '0.1', min: '0' }}
        />
        <SelectField
          dense
          label="Estado"
          value={estado}
          onChange={e => setEstado(e.target.value)}
        >
          <option value="active">Activa</option>
          <option value="closed">Cerrada</option>
        </SelectField>
      </div>

      <p className="text-xs text-content-subtle">
        La fecha de término es el corte del período: desde ahí el saldo se sigue ganando día a día.
      </p>

      {error && (
        <div className="text-xs text-danger-content bg-danger-surface border border-danger-border rounded-lg p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose}
          className="px-3 py-1.5 text-xs font-semibold text-content-muted hover:text-content">
          Cancelar
        </button>
        <button onClick={guardar} disabled={creando || actualizando}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
          {creando || actualizando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}
